/**
 * Locale-aware single-word labels (Australian vs US English).
 * Build phrases in code: e.g. `Email ${t('customisation')}`, `Manage ${t('organisation')}`.
 */

import type { Locale } from '@/constant/locale'

export const LOCALE_LABELS = {
  customisation: { 'en-AU': 'Customisation', 'en-US': 'Customization' },
  organisation: { 'en-AU': 'Organisation', 'en-US': 'Organization' },
  organisationLower: { 'en-AU': 'organisation', 'en-US': 'organization' },
  organisations: { 'en-AU': 'Organisations', 'en-US': 'Organizations' },
  organisationsLower: { 'en-AU': 'organisations', 'en-US': 'organizations' },
  prioritisation: { 'en-AU': 'prioritisation', 'en-US': 'prioritization' },
  customise: { 'en-AU': 'Customise', 'en-US': 'Customize' },
  customiseLower: { 'en-AU': 'customise', 'en-US': 'customize' },
  colour: { 'en-AU': 'colour', 'en-US': 'color' },
  centre: { 'en-AU': 'Centre', 'en-US': 'Center' },
  centreLower: { 'en-AU': 'centre', 'en-US': 'center' },
  behaviour: { 'en-AU': 'behaviour', 'en-US': 'behavior' },
  unauthorised: { 'en-AU': 'unauthorised', 'en-US': 'unauthorized' },
  unauthorisedTitle: { 'en-AU': 'Unauthorised', 'en-US': 'Unauthorized' },
  defences: { 'en-AU': 'defences', 'en-US': 'defenses' },
} satisfies Record<string, Record<Locale, string>>

export type LocaleLabelKey = keyof typeof LOCALE_LABELS

/** Sidebar title (en-AU) -> build from words. Parts are literal strings or locale keys. */
export const SIDEBAR_TITLE_PARTS: Partial<Record<string, (string | LocaleLabelKey)[]>> = {
  'Manage Organisations': ['Manage ', 'Organisations'],
  'Organisations List': ['Organisations', ' List'],
  'Organisation Users': ['Organisation', ' Users'],
  'Organisation Subscription': ['Organisation', ' Subscription'],
}

/** Build sidebar display title from parts using t() */
export const buildSidebarTitle = (enAuTitle: string, t: (key: LocaleLabelKey) => string): string => {
  const parts = SIDEBAR_TITLE_PARTS[enAuTitle]
  if (!parts) return enAuTitle
  return parts.map((p) => (typeof p === 'string' ? p : t(p))).join('')
}

/** Breadcrumb label (en-AU) -> build from words */
export const BREADCRUMB_LABEL_PARTS: Partial<Record<string, (string | LocaleLabelKey)[]>> = {
  'Manage Organisation': ['Manage ', 'Organisation'],
  'Manage Organisation Risk': ['Manage ', 'Organisation', ' Risk'],
  'Organisation Onboarding': ['Organisation', ' Onboarding'],
  Organisations: ['Organisations'],
  'Organisation Management': ['Organisation', ' Management'],
  'Organisation Subscription': ['Organisation', ' Subscription'],
}

/** Build breadcrumb label from parts using t() */
export const buildBreadcrumbLabel = (enAuLabel: string, t: (key: LocaleLabelKey) => string): string => {
  const parts = BREADCRUMB_LABEL_PARTS[enAuLabel]
  if (!parts) return enAuLabel
  return parts.map((p) => (typeof p === 'string' ? p : t(p))).join('')
}

export const getLocaleLabel = (key: LocaleLabelKey, locale: Locale): string => {
  const labels = LOCALE_LABELS[key]
  if (!labels) return key
  return labels[locale] ?? labels['en-AU'] ?? key
}
